/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {
    Component, ElementRef, HostBinding,
    HostListener, OnDestroy, OnInit, ViewEncapsulation
} from '@angular/core';
import {EditorView} from 'prosemirror-view';
import {Subscription} from 'rxjs';
import {EditorState, Transaction} from 'prosemirror-state';
import {FcAgEditorService} from '../../../editor.service';
import {MenuService} from '../menu.service';
import Icon from '../../../icons';
import {
    addColumnAfter,
    addColumnBefore,
    addRowAfter,
    addRowBefore,
    addTable,
    deleteColumn,
    deleteRow, deleteTable
} from '../../table/commands';
import {isInTable} from '../../table';

/**
 * Dropdown component.
 */
@Component({
    selector: 'fc-table-menu',
    templateUrl: './tableMenu.component.html',
    styleUrls: ['./tableMenu.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class TableMenuComponent implements OnInit, OnDestroy {
    private editorView: EditorView;

    private updateSubscription: Subscription;

    /**
     *
     */
    html = Icon.get('table');
    
    /**
     *
     */
    isTableSelected: boolean = false;

    /**
     * Items.
     */
    items: any[] = [
        {
            label: 'Insert table',
            onClick: (state: EditorState, dispatch: (tr: Transaction) => void) => {
                addTable(state, dispatch, {rowsCount: 2, colsCount: 3, withHeaderRow: true, cellContent: ''});
            },
            isDisabled: () => {
                return this.isTableSelected;   
            }
        },
        {
            label: 'Insert column before',
            onClick: (state: EditorState, dispatch: (tr: Transaction) => void) => {
                addColumnBefore(state, dispatch);
            },
            isDisabled: () => {
                return !this.isTableSelected;
            }
        },
        {
            label: 'Insert column after',
            onClick: (state: EditorState, dispatch: (tr: Transaction) => void) => {
                addColumnAfter(state, dispatch);
            },
            isDisabled: () => {
                return !this.isTableSelected;
            }
        },
        {
            label: 'Delete column',
            onClick: (state: EditorState, dispatch: (tr: Transaction) => void) => {
                deleteColumn(state, dispatch);
            },
            isDisabled: () => {
                return !this.isTableSelected;
            }
        },
        {
            label: 'Insert row before',
            onClick: (state: EditorState, dispatch: (tr: Transaction) => void) => {
                addRowBefore(state, dispatch);
            },
            isDisabled: () => {
                return !this.isTableSelected;
            }
        },
        {
            label: 'Insert row after',
            onClick: (state: EditorState, dispatch: (tr: Transaction) => void) => {
                addRowAfter(state, dispatch);
            },
            isDisabled: () => {
                return !this.isTableSelected;
            }
        },
        {
            label: 'Delete row',
            onClick: (state: EditorState, dispatch: (tr: Transaction) => void) => {
                deleteRow(state, dispatch);
            },
            isDisabled: () => {
                return !this.isTableSelected;
            }
        },
        {
            label: 'Delete table',
            onClick: (state: EditorState, dispatch: (tr: Transaction) => void) => {
                deleteTable(state, dispatch);
            },
            isDisabled: () => {
                return !this.isTableSelected;
            }
        }
    ];

    /**
     * Is dropdown open.
     */
    isDropdownOpen = false;

    /**
     * Disabled items.
     */
    disabledItems: string[] = [];

    /**
     * Active Item.
     */
    activeItem: string | null;

    /**
     * Default constructor.
     * 
     * @param fcAgEditorService FcAgEditorService.
     * @param menuService Menu service.
     * @param el Element Reference.
     */
    constructor(
        private fcAgEditorService: FcAgEditorService,
        private menuService: MenuService,
        private el: ElementRef
    ) {
    }

    /**
     * Is selected binding.
     * 
     * @returns Boolean.
     */
    @HostBinding('class.FcAgEditor__Dropdown--Selected') get isSelected(): boolean {
        return Boolean(this.activeItem || this.isDropdownOpen);
    }

    /**
     * Is dropdown disabled.
     * 
     * @returns Boolean.
     */
    @HostBinding('class.FcAgEditor--Disabled') get isDropdownDisabled(): boolean {
        return false;
    }

    /**
     * On document click.
     * 
     * @param target Node.
     */
    @HostListener('document:mousedown', ['$event.target']) onDocumentClick(target: Node): void {
        if (!this.el.nativeElement.contains(target) && this.isDropdownOpen) {
            this.isDropdownOpen = false;
        }
    }

    /**
     * Toggle dropdown.
     * 
     * @param e Mouse event.
     */
    toggleDropdown(e: MouseEvent): void {
        e.preventDefault();
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    /**
     * Track by index.
     * 
     * @param index Number.
     * @returns Number.
     */
    trackByIndex(index: number): number {
        return index;
    }

    /**
     * On click.
     * 
     * @param e Mouse event.
     * @param item Item.
     */
    onClick(e: MouseEvent, item: any): void {
        e.preventDefault();

        if (item.isDisabled()) {
            return;
        }
        
        // consider only left click
        if (e.button !== 0) {
            return;
        }
        
        const {state, dispatch} = this.editorView;
        item.onClick(state, dispatch);
        
        this.isDropdownOpen = false;
    }

    private update = (view: EditorView) => {
        this.isTableSelected = isInTable(view.state);
    }

    /**
     * Angular life cycle.
     */
    ngOnInit(): void {
        this.editorView = this.menuService.editor.view;

        this.updateSubscription = this.menuService.editor.update.subscribe((view: EditorView) => {
            this.update(view);
        });
    }

    /**
     * Angular life cycle.
     */
    ngOnDestroy(): void {
        this.updateSubscription.unsubscribe();
    }
}
